import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from './AccountVerification.module.css';
import ContentApp from '../../components/ContentApp';
import Header from '../../components/Common/Header/Header';
import { useContext, useEffect, useState } from 'react';
import { getAssociateSaleSetup } from '../../actions/associatesActions';
import CompletedIcon from '../../components/icons/CompletedIcon';
import SendIcon from '../../components/icons/SendIcon';
import RejectedIcon from '../../components/icons/RejectedIcon';
import PendingIcon from '../../components/icons/PendingIcon';
import WarningIconVerification from '../../components/icons/WarningIconVerification';
import CheckIcon from '../../components/icons/CheckIcon';
import { WelletContext } from '../../context/WelletContext';
import Modal from '../../components/Common/Modal/Modal';
import { InputSingleCharacter } from '../../components/Common/InputSingleCharacter/InputSingleCharacter';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import { Spinner } from '../../components/Common/Spinner/Spinner';
import useRealtimeVerification from '../../hooks/useRealtimeVerification';

const AccountVerification = ({ history }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.associates.user);
  useRealtimeVerification();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('idle');

  const dispatch = useDispatch();
  const affiliateType = useSelector((state) => state.associates.user.affiliateType);
  const associates = useSelector((state) => state.associates);
  const welletContext = useContext(WelletContext);
  const email = useSelector((state) => state.associates?.user?.email);
  const redirectPath = () => {
    const methodType = user?.withdrawalMethod;
    switch (methodType) {
      case 'MERCADOPAGO':
        return '/mercadopagoaccount';
      case 'WIRE_MEXICO':
        return '/national-bank';
      case 'WIRE_INTERNATIONAL':
        return '/international-bank';
      default:
        return null;
    }
  };

  const handleResendEmail = async () => {
    setEmailCode('');
    setEmailError('');
    setVerificationStatus('idle');

    try {
      await welletContext.apis.tickets.post(`/users/email-verification`);
      setShowEmailModal(true);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (showEmailModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showEmailModal]);

  const handleVerifyEmailCode = async () => {
    if (emailCode.length !== 4) return;

    setIsVerifyingEmail(true);
    setEmailError('');
    setVerificationStatus('verifying');

    try {
      const result = await welletContext.apis.tickets.post(`/users/email-verification/validate`, {
        code: emailCode,
      });

      if (result?.data?.isValid) {
        setVerificationStatus('success');
        setTimeout(() => {
          setShowEmailModal(false);
          dispatch(getAssociateSaleSetup(associates.jwt));
          setVerificationStatus('idle');
          setEmailCode('');
        }, 3000);
      } else {
        setVerificationStatus('idle');
        setEmailError('Código incorrecto');
      }
    } catch (error) {
      setVerificationStatus('idle');
      setEmailError('Hubo un error, intenta nuevamente.');
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const getButtonLabel = (type, status) => {
    if (status === 'REJECTED' || status === 'REVALIDATION') return t(`${type}.retry`);
    switch (type) {
      case 'KYC':
        return t('KYC.verify');
      case 'WORK_CREDENTIALS':
        return t('WORK_CREDENTIALS.upload');
      case 'WITHDRAWAL_METHOD':
        return t('WITHDRAWAL_METHOD.linkAccount');
      case 'EMAIL':
        return t('EMAIL.verify');
      default:
        return t(`${type}.verify`);
    }
  };

  const verificationRedirect = {
    PHONE: '/phone-verification',
    EMAIL: handleResendEmail,
    KYC: '/kyc-start',
    WORK_CREDENTIALS: '/gafete-concierge',
    WITHDRAWAL_METHOD: redirectPath(),
  };

  const rawList = user.verificationUser;

  const listVerificationUser =
    affiliateType === 'CONCIERGE' || affiliateType === 'INTERNAL_STAFF'
      ? rawList
      : rawList.filter((item) => item.type !== 'WORK_CREDENTIALS');

  const completedSteps = listVerificationUser.filter((item) => item.status === 'COMPLETED').length;
  const totalSteps = listVerificationUser.length;
  const percentage = Math.round((completedSteps / totalSteps) * 100);
  const isCompleted = percentage === 100;

  const footerMessageFinal = isCompleted
    ? affiliateType === 'CONCIERGE'
      ? t('footerMessageVerifiedConcierge')
      : affiliateType === 'INTERNAL_STAFF'
      ? t('footerMessageInternalStaff')
      : t('footerMessageVerifiedIndependent')
    : t('footerMessage');

  const getDescription = (type, status) => {
    if (type === 'WORK_CREDENTIALS' && status === 'SEND') {
      return t('WORK_CREDENTIALS.evaluating');
    }

    const isIncomplete = status === 'PENDING' || status === 'REJECTED' || status === 'REVALIDATION';
    return isIncomplete ? t(`${type}.incomplete`) : t(`${type}.completed`);
  };

  const getIcon = (type, status) => {
    if (status === 'COMPLETED') return <CompletedIcon className={styles.iconCompleted} />;
    if (status === 'SEND') return <SendIcon className={styles.iconSend} />;
    if (status === 'REJECTED' || status === 'REVALIDATION')
      return <RejectedIcon className={styles.iconRejected} />;
    if (type === 'WORK_CREDENTIALS' && status === 'PENDING')
      return <PendingIcon className={styles.iconPending} />;
    return <WarningIconVerification className={styles.iconWarning} />;
  };

  useEffect(() => {
    dispatch(getAssociateSaleSetup(associates.jwt));
  }, [dispatch, associates.jwt]);

  return (
    <>
      {showEmailModal && (
        <Modal
          open={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEmailCode('');
            setEmailError('');
            setVerificationStatus('idle');
            setIsVerifyingEmail(false);
          }}
          hasBackground
          animationClass='fade-up-horizontal'
        >
          <div>
            <div className='text-center '>
              <div className={styles.emailIconWrapper}>
                <svg width='48' height='48' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M3 8L10.89 13.26C11.54 13.67 12.46 13.67 13.11 13.26L21 8M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <h3 className='text-primary bold font-semi-large'>{t('emailVerificationTitle')}</h3>
              <div className='text-medium-gray font-tiny '>{t('enterEmailCode')}</div>
              <div className='text-medium-gray mb-3'>{email}</div>
            </div>

            {(verificationStatus === 'verifying' || verificationStatus === 'success') && (
              <div className={`${styles.statusOverlay} ${styles[verificationStatus]}`}>
                {verificationStatus === 'verifying' && (
                  <div className='d-flex justify-content-center'>
                    <Spinner size={25} color='#fff' />
                  </div>
                )}
                {verificationStatus === 'success' && (
                  <div className={styles.successIcon}>
                    <svg width='80' height='80' viewBox='0 0 24 24' fill='none'>
                      <circle
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='#10b981'
                        strokeWidth='2'
                        fill='none'
                        className={styles.checkCircle}
                      />
                      <path
                        d='M8 12.5L10.5 15L16 9.5'
                        stroke='#10b981'
                        strokeWidth='2.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className={styles.checkMark}
                      />
                    </svg>
                    <p className={styles.successText}>{t('emailVerified')}</p>
                  </div>
                )}
              </div>
            )}

            <div className='mb-3'>
              <InputSingleCharacter
                length={4}
                onValueChange={(val) => {
                  setEmailCode(val);
                  setEmailError('');
                  if (verificationStatus === 'error') setVerificationStatus('idle');
                }}
                error={!!emailError}
              />
            </div>
            <div className=' foreign-support-note p-1 mb-3'>
              <span className='asterisk-icon'>*</span>
              <span>
                {t('contactEmailAddressFrom')}{' '}
                <button
                  type='button'
                  className='support-link'
                  onClick={() => history.push('/email-revalidation-profile')}
                >
                  {t('here')}
                </button>
                {'.'}
              </span>
            </div>

            {emailError && (
              <div className='mt-2'>
                <ErrorBox title={emailError} />
              </div>
            )}

            <button
              className={`${styles.verifyButton} ${
                emailCode.length === 4 ? styles.verifyButtonActive : ''
              }`}
              disabled={emailCode.length !== 4 || isVerifyingEmail}
              onClick={handleVerifyEmailCode}
            >
              {isVerifyingEmail ? (
                <span className={styles.buttonContent}>
                  <div className='d-flex justify-content-center'>
                    <Spinner size={15} color='#fff' />
                  </div>
                  {t('verifying')}
                </span>
              ) : (
                t('validateCode')
              )}
            </button>
          </div>
        </Modal>
      )}

      <Header
        title={<div className='bold'>{t('accountVerification')}</div>}
        history={history}
        hasBackground={true}
        pathName='/'
      />

      <ContentApp classes='pt-0'>
        <div className={styles.screenFadeIn}>
          <div className='text-lighter-gray semi-bold mb-3'>{t('completeStepsDescription')}</div>

          <div>
            <div className={styles.progressBarWrapper}>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${percentage}%` }}>
                  <div className={styles.progressShine}></div>
                </div>
              </div>
              {percentage < 100 && <div className={styles.progressPulse}></div>}
            </div>
            <p className='text-lighter-gray bold text-center'>
              {t('progressCompleted', { percentage })}
            </p>
          </div>

          <div
            className={styles.verificationList}
            style={{ '--count': listVerificationUser.length }}
          >
            {listVerificationUser.map((item, index) => {
              const showButton = ['PENDING', 'REJECTED', 'REVALIDATION'].includes(item.status);
              return (
                <div
                  key={index}
                  className={styles.verificationItem}
                  style={{ '--index': index }}
                  onClick={() => {
                    if (!showButton) return;

                    if (item.type === 'EMAIL') {
                      verificationRedirect[item.type]();
                    } else {
                      history.push(verificationRedirect[item.type]);
                    }
                  }}
                >
                  <div className={styles.iconContainer}>{getIcon(item.type, item.status)}</div>
                  <div className={styles.content}>
                    <div className={styles.itemHeader}>
                      <div className={styles.itemTitle}>
                        {item.type === 'WITHDRAWAL_METHOD'
                          ? t(`WITHDRAWAL_METHOD_TYPE.${user?.withdrawalMethod}`)
                          : t(`${item.type}.title`)}
                      </div>
                    </div>
                    <div className={styles.itemBody}>
                      <p className={styles.itemDescription}>
                        {getDescription(item.type, item.status)}
                      </p>
                      {showButton && (
                        <button className={styles.actionButton}>
                          {getButtonLabel(item.type, item.status)}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`${styles.footer} ${isCompleted ? styles.footerCompleted : ''}`}>
            {isCompleted ? (
              <CheckIcon checkColor='#000' circleColor='#10b981' size={50} />
            ) : (
              <CheckIcon checkColor='#000' circleColor='#6B7280' size={35} />
            )}
            <p className={styles.footerText}>{footerMessageFinal}</p>
          </div>
        </div>
      </ContentApp>
    </>
  );
};

export default AccountVerification;
